if (!customElements.get('fantasia-nilu-swatches')) {
  customElements.define(
    'fantasia-nilu-swatches',
    class FantasiaNiluSwatches extends HTMLElement {
      connectedCallback() {
        this.variantSelects = this.closest('variant-selects');
        this.variants = this.parseVariants();
        this.onClick = this.onClick.bind(this);
        this.onVariantSelectsChange = this.onVariantSelectsChange.bind(this);
        this.modal = this.ensurePreviewModal();
        this.modalImage = this.modal?.querySelector('[data-fantasia-modal-image]');
        this.modalCaption = this.modal?.querySelector('[data-fantasia-modal-caption]');
        this.filterSwatchesForProduct();
        this.sortSwatches();
        this.addEventListener('click', this.onClick);
        this.variantSelects?.addEventListener('change', this.onVariantSelectsChange);
        this.bindProductForm();
        this.syncRememberedSelection();
        this.updateInventoryBadges();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.variantSelects?.removeEventListener('change', this.onVariantSelectsChange);
      }

      parseVariants() {
        const script = this.querySelector('[data-fantasia-variants]');
        if (!script) return [];

        try {
          return JSON.parse(script.textContent);
        } catch {
          return [];
        }
      }

      filterSwatchesForProduct() {
        const optionIndex = Number(this.dataset.optionIndex);
        const productFantasyValues = new Set(this.variants.map((variant) => variant.options[optionIndex]));
        const renderedSwatches = Array.from(this.querySelectorAll('[data-fantasia-swatch]'));
        const seenValues = new Set();

        renderedSwatches.forEach((swatch) => {
          const fantasyValue = swatch.dataset.fantasiaValue;
          if (seenValues.has(fantasyValue) || !productFantasyValues.has(fantasyValue)) {
            swatch.remove();
            return;
          }
          seenValues.add(fantasyValue);
        });

        this.querySelectorAll('.fantasia-swatches__family').forEach((family) => {
          if (!family.querySelector('[data-fantasia-swatch]')) {
            family.remove();
          }
        });
      }

      sortSwatches() {
        this.querySelectorAll('[data-fantasia-grid]').forEach((grid) => {
          Array.from(grid.querySelectorAll('[data-fantasia-swatch]'))
            .sort((first, second) =>
              first.dataset.fantasiaName.localeCompare(second.dataset.fantasiaName, undefined, {
                sensitivity: 'base',
              })
            )
            .forEach((swatch) => grid.appendChild(swatch));
        });
      }

      onClick(event) {
        if (event.target.closest('[data-fantasia-modal-close]')) {
          this.closePreview();
          return;
        }

        const swatch = event.target.closest('[data-fantasia-swatch]');
        if (!swatch || swatch.disabled) return;

        const variant = this.findBestVariant(swatch.dataset.fantasiaValue);
        if (!variant) return;

        const changedControl = this.applyVariantOptions(variant);
        if (!changedControl) return;

        this.clearProductFormError();
        this.rememberSelection(swatch.dataset.fantasiaValue);
        changedControl.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncSelection(swatch.dataset.fantasiaValue, swatch.dataset.fantasiaName);
        this.openPreview(swatch);
      }

      openPreview(swatch) {
        this.modal = this.ensurePreviewModal();
        this.modalImage = this.modal?.querySelector('[data-fantasia-modal-image]');
        this.modalCaption = this.modal?.querySelector('[data-fantasia-modal-caption]');

        if (!this.modal || !this.modalImage || !swatch.dataset.fantasiaImage) return;

        this.lastFocusedElement = document.activeElement;
        this.modalImage.src = swatch.dataset.fantasiaImage;
        this.modalImage.alt = swatch.dataset.fantasiaName || '';

        if (this.modalCaption) {
          this.modalCaption.textContent = swatch.dataset.fantasiaName || '';
        }

        this.modal.hidden = false;
        this.modal.querySelector('[data-fantasia-modal-close]')?.focus();
      }

      closePreview() {
        if (!this.modal) return;

        this.closeModalElement(this.modal);
      }

      ensurePreviewModal() {
        let modal = document.querySelector('[data-fantasia-global-modal]');
        const localModal = this.querySelector('[data-fantasia-modal]');

        if (!modal && localModal) {
          modal = localModal;
          modal.setAttribute('data-fantasia-global-modal', 'true');
          document.body.appendChild(modal);
        } else if (localModal) {
          localModal.remove();
        }

        if (modal) {
          this.bindPreviewModal(modal);
        }

        return modal;
      }

      bindPreviewModal(modal) {
        if (modal.dataset.fantasiaModalBound === 'true') return;

        modal.addEventListener('click', (event) => {
          if (event.target.closest('[data-fantasia-modal-close]')) {
            this.closeModalElement(modal);
          }
        });

        if (!window.__fantasiaNiluModalKeydownBound) {
          document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') return;

            const openModal = document.querySelector('[data-fantasia-global-modal]:not([hidden])');
            if (openModal) {
              this.closeModalElement(openModal);
            }
          });

          window.__fantasiaNiluModalKeydownBound = true;
        }

        modal.dataset.fantasiaModalBound = 'true';
      }

      closeModalElement(modal) {
        modal.hidden = true;

        const modalImage = modal.querySelector('[data-fantasia-modal-image]');
        const modalCaption = modal.querySelector('[data-fantasia-modal-caption]');

        if (modalImage) {
          modalImage.removeAttribute('src');
          modalImage.alt = '';
        }

        if (modalCaption) {
          modalCaption.textContent = '';
        }

        if (this.lastFocusedElement instanceof HTMLElement) {
          this.lastFocusedElement.focus();
        }
      }

      findBestVariant(fantasyValue) {
        const optionIndex = Number(this.dataset.optionIndex);
        const selectedOptions = this.getCurrentOptions();
        const preferredOptions = [...selectedOptions];
        preferredOptions[optionIndex] = fantasyValue;

        return (
          this.variants.find((variant) => this.optionsMatch(variant.options, preferredOptions)) ||
          this.variants.find((variant) => variant.available && variant.options[optionIndex] === fantasyValue) ||
          this.variants.find((variant) => variant.options[optionIndex] === fantasyValue)
        );
      }

      onVariantSelectsChange() {
        const fantasyValue = this.getCurrentOptions()[Number(this.dataset.optionIndex)];

        if (this.hasUserSelection() && fantasyValue) {
          this.syncSelection(fantasyValue);
        } else {
          this.updateInventoryBadges();
        }
      }

      getCurrentOptions() {
        if (!this.variantSelects) return [];

        return Array.from(
          this.variantSelects.querySelectorAll('.product-form__input select, fieldset.product-form__input')
        ).map((controlGroup) => {
          if (controlGroup.matches('select')) {
            return controlGroup.value;
          }

          return controlGroup.querySelector('input[type="radio"]:checked')?.value || '';
        });
      }

      optionsMatch(firstOptions, secondOptions) {
        return firstOptions.every((option, index) => option === secondOptions[index]);
      }

      applyVariantOptions(variant) {
        if (!this.variantSelects) return null;

        let changedControl = null;

        variant.options.forEach((optionValue, index) => {
          const position = index + 1;
          const fieldset = this.variantSelects.querySelector(`fieldset[data-option-position="${position}"]`);
          const select = this.variantSelects.querySelector(`select[data-option-position="${position}"]`);

          if (fieldset) {
            const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
              (radio) => radio.value === optionValue
            );
            if (input && !input.checked) {
              input.checked = true;
              changedControl = input;
            }
          } else if (select && select.value !== optionValue) {
            select.value = optionValue;
            changedControl = select;
          }
        });

        return changedControl || this.getFantasyControl(variant.options[Number(this.dataset.optionIndex)]);
      }

      getFantasyControl(fantasyValue) {
        const position = Number(this.dataset.optionIndex) + 1;
        const fieldset = this.variantSelects?.querySelector(`fieldset[data-option-position="${position}"]`);
        const select = this.variantSelects?.querySelector(`select[data-option-position="${position}"]`);

        if (fieldset) {
          return Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
            (radio) => radio.value === fantasyValue
          );
        }

        return select || null;
      }

      bindProductForm() {
        const productForm = this.closest('product-info')?.querySelector('product-form');
        const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return;

        form.addEventListener(
          'submit',
          (event) => {
            if (this.hasUserSelection()) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            productForm.handleErrorMessage?.('Seleziona la fantasia');
          },
          true
        );
      }

      hasUserSelection() {
        return Boolean(this.getRememberedSelection() || this.querySelector('[data-fantasia-swatch].is-selected'));
      }

      clearProductFormError() {
        this.closest('product-info')?.querySelector('product-form')?.handleErrorMessage?.();
      }

      getSelectionKey() {
        return `${window.location.pathname}:${this.dataset.section}:${this.dataset.optionIndex}`;
      }

      rememberSelection(fantasyValue) {
        window.__fantasiaNiluSelections = window.__fantasiaNiluSelections || {};
        window.__fantasiaNiluSelections[this.getSelectionKey()] = fantasyValue;
      }

      getRememberedSelection() {
        const fantasyValue = window.__fantasiaNiluSelections?.[this.getSelectionKey()];
        if (!fantasyValue) return '';

        const hasSwatch = Array.from(this.querySelectorAll('[data-fantasia-swatch]')).some(
          (swatch) => swatch.dataset.fantasiaValue === fantasyValue
        );

        return hasSwatch ? fantasyValue : '';
      }

      syncRememberedSelection() {
        const fantasyValue = this.getRememberedSelection();

        if (fantasyValue) {
          this.syncSelection(fantasyValue);
          return;
        }

        this.clearVisualSelection();
      }

      clearVisualSelection() {
        this.querySelector('[data-fantasia-selected]').textContent = '';
        this.querySelectorAll('[data-fantasia-swatch]').forEach((swatch) => {
          swatch.classList.remove('is-selected');
          swatch.setAttribute('aria-pressed', 'false');
        });
      }

      syncSelection(fantasyValue, fantasyName = '') {
        const selectedSwatch = Array.from(this.querySelectorAll('[data-fantasia-swatch]')).find(
          (swatch) => swatch.dataset.fantasiaValue === fantasyValue
        );

        this.querySelector('[data-fantasia-selected]').textContent =
          fantasyName || selectedSwatch?.dataset.fantasiaName || '';
        this.querySelectorAll('[data-fantasia-swatch]').forEach((swatch) => {
          const isSelected = swatch.dataset.fantasiaValue === fantasyValue;
          swatch.classList.toggle('is-selected', isSelected);
          swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

          if (isSelected) {
            swatch.closest('details')?.setAttribute('open', '');
          }
        });
        this.updateInventoryBadges();
      }

      findVariantForFantasy(fantasyValue) {
        const optionIndex = Number(this.dataset.optionIndex);
        const selectedOptions = this.getCurrentOptions();
        const preferredOptions = [...selectedOptions];
        preferredOptions[optionIndex] = fantasyValue;

        return this.variants.find((variant) => this.optionsMatch(variant.options, preferredOptions));
      }

      getInventoryBadgeState(variant) {
        if (!variant) return null;
        if (variant.inventory_management !== 'shopify') return null;

        const inventoryQuantity = Number(variant.inventory_quantity);
        if (Number.isNaN(inventoryQuantity)) return null;

        if (inventoryQuantity === 0) {
          return {
            className: 'is-sold-out',
            text: 'Esaurito',
          };
        }

        if (inventoryQuantity === 1) {
          return {
            className: 'is-low-stock',
            text: 'Pochi pezzi',
          };
        }

        return null;
      }

      updateBadgeElement(badge, badgeState) {
        if (!badge) return;

        badge.classList.remove('is-sold-out', 'is-low-stock');

        if (!badgeState) {
          badge.textContent = '';
          badge.hidden = true;
          return;
        }

        badge.textContent = badgeState.text;
        badge.classList.add(badgeState.className);
        badge.hidden = false;
      }

      updateInventoryBadges() {
        const selectedSwatch = this.querySelector('[data-fantasia-swatch].is-selected');
        const selectedVariant = selectedSwatch ? this.findVariantForFantasy(selectedSwatch.dataset.fantasiaValue) : null;
        const selectedBadgeState = this.getInventoryBadgeState(selectedVariant);

        this.updateBadgeElement(
          this.querySelector('[data-fantasia-selected-badge]'),
          selectedBadgeState
        );
        this.updateSelectedSwatchInventoryDot(selectedSwatch, selectedBadgeState);
      }

      updateSelectedSwatchInventoryDot(selectedSwatch, selectedBadgeState) {
        this.querySelectorAll('[data-fantasia-inventory-dot]').forEach((dot) => {
          dot.classList.remove('is-sold-out', 'is-low-stock');
          dot.hidden = true;
        });

        if (!selectedSwatch || !selectedBadgeState) return;

        const dot = selectedSwatch.querySelector('[data-fantasia-inventory-dot]');
        if (!dot) return;

        dot.classList.add(selectedBadgeState.className);
        dot.hidden = false;
      }
    }
  );
}
